import type {
  CaseStatus,
  ChecklistDto,
  ChecklistListItemDto,
  ChecklistSortField,
  CreateChecklistInput,
  ReplaceChecklistItemsInput,
  UpdateChecklistInput,
} from '@tms/shared';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';

export interface ChecklistListParams {
  page?: number;
  perPage?: number;
  folderId?: string;
  includeSubfolders?: boolean;
  status?: CaseStatus;
  labelId?: string;
  q?: string;
  sort?: ChecklistSortField;
  order?: 'asc' | 'desc';
}

export const checklistsQueryOptions = (projectCode: string, params: ChecklistListParams) =>
  queryOptions({
    queryKey: queryKeys.checklists.list(projectCode, params),
    queryFn: ({ signal }) =>
      api.list<ChecklistListItemDto>(`/projects/${projectCode}/checklists`, { ...params }, signal),
    placeholderData: (previous) => previous,
  });

export const checklistQueryOptions = (projectCode: string, key: string) =>
  queryOptions({
    queryKey: queryKeys.checklists.detail(projectCode, key),
    queryFn: ({ signal }) =>
      api.get<ChecklistDto>(`/projects/${projectCode}/checklists/${key}`, undefined, signal),
  });

/** `enabled` lets closed dialogs mount without fetching their pickers. */
export const useChecklists = (
  projectCode: string,
  params: ChecklistListParams,
  options: { enabled?: boolean } = {},
) => useQuery({ ...checklistsQueryOptions(projectCode, params), enabled: options.enabled ?? true });

export const useChecklist = (projectCode: string, key: string) =>
  useQuery(checklistQueryOptions(projectCode, key));

const useChecklistInvalidation = (projectCode: string) => {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.checklists.all(projectCode) });
    // Folder counts in the tree beside the list move with the checklist.
    void queryClient.invalidateQueries({
      queryKey: queryKeys.folders.tree(projectCode, 'checklist'),
    });
    void queryClient.invalidateQueries({ queryKey: queryKeys.projects.overview(projectCode) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
  };
};

export const useCreateChecklist = (projectCode: string) => {
  const invalidate = useChecklistInvalidation(projectCode);
  return useMutation({
    mutationFn: (input: CreateChecklistInput) =>
      api.post<ChecklistDto>(`/projects/${projectCode}/checklists`, input),
    onSuccess: invalidate,
  });
};

export const useUpdateChecklist = (projectCode: string, checklistId: string) => {
  const invalidate = useChecklistInvalidation(projectCode);
  return useMutation({
    mutationFn: (input: UpdateChecklistInput) =>
      api.patch<ChecklistDto>(`/projects/${projectCode}/checklists/${checklistId}`, input),
    onSuccess: invalidate,
  });
};

/** The editor always sends the complete item list (add/edit/remove/reorder). */
export const useReplaceChecklistItems = (projectCode: string, checklistId: string) => {
  const invalidate = useChecklistInvalidation(projectCode);
  return useMutation({
    mutationFn: (input: ReplaceChecklistItemsInput) =>
      api.put<ChecklistDto>(`/projects/${projectCode}/checklists/${checklistId}/items`, input),
    onSuccess: invalidate,
  });
};

export const useDuplicateChecklist = (projectCode: string) => {
  const invalidate = useChecklistInvalidation(projectCode);
  return useMutation({
    mutationFn: (checklistId: string) =>
      api.post<ChecklistDto>(`/projects/${projectCode}/checklists/${checklistId}/duplicate`),
    onSuccess: invalidate,
  });
};

export const useDeleteChecklist = (projectCode: string) => {
  const invalidate = useChecklistInvalidation(projectCode);
  return useMutation({
    mutationFn: (checklistId: string) =>
      api.delete(`/projects/${projectCode}/checklists/${checklistId}`),
    onSuccess: invalidate,
  });
};
